import { DEFAULT_TEMPO_ID } from '@kolony/shared';
import { z } from 'zod';

// Versioned localStorage wrapper (seed, verbatim mechanism; schemas re-cut for Kolony per
// REUSE-MAP §6 — FAFI's football fields and the D138 tutorial store died with the football
// half). Every stored blob carries `v`; a corrupt or unknown-version value is reset to the
// default and the raw preserved under `<key>.bak` for recovery. Never throws — bad storage
// degrades to defaults, never a crash. The storage area is injected so tests use a fake.

export interface StorageArea {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class VersionedStore<T> {
  constructor(
    private readonly key: string,
    private readonly schema: z.ZodType<T>,
    private readonly fallback: () => T,
    private readonly area: StorageArea,
    /** Upgrade an older-version blob to the current shape before validation. */
    private readonly migrate: (raw: unknown) => unknown = (raw) => raw,
  ) {}

  /**
   * Load the value: an older-version blob is MIGRATED forward (data preserved); an absent,
   * corrupt, or still-invalid-after-migration (e.g. unknown FUTURE version) blob resets to the
   * default, preserving the raw as `.bak`.
   */
  load(): T {
    const raw = this.area.getItem(this.key);
    if (raw === null) return this.fallback();
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return this.reset(raw);
    }
    const result = this.schema.safeParse(this.migrate(parsed));
    return result.success ? result.data : this.reset(raw);
  }

  save(value: T): void {
    this.area.setItem(this.key, JSON.stringify(value));
  }

  clear(): void {
    this.area.removeItem(this.key);
  }

  private reset(corruptRaw: string): T {
    // load() must NEVER throw (a quota-exhausted / private-mode area can throw from setItem/
    // removeItem) — a failed .bak or clear still degrades to defaults rather than crashing.
    try {
      this.area.setItem(`${this.key}.bak`, corruptRaw); // keep the bad data for recovery/debugging
      this.area.removeItem(this.key);
    } catch {
      /* storage unavailable — fall through to defaults */
    }
    return this.fallback();
  }
}

// ── Schemas (plan §16.1) ─────────────────────────────────────────────────────

/** Guest identity + client-side career totals (the H5 guest→account import source, plan §14.1). */
export const guestSchema = z.object({
  v: z.literal(1),
  guestId: z.string(),
  name: z.string(),
  totals: z.object({ games: z.number().int().nonnegative(), wins: z.number().int().nonnegative() }).optional(),
});
export type GuestState = z.infer<typeof guestSchema>;

/**
 * A brand-new guest (brief §6.6): 16 random bytes as 32 lowercase hex, named from the first four.
 *
 * D260 made the format load-bearing. The server files a telemetry batch it cannot verify ONLY
 * under an id of exactly this shape (`isGuestId`), so a generator the predicate refused would
 * drop every guest's telemetry in silence, every request still answered 204. It lives here
 * rather than in main.ts so that T-UI-140 can hold the two together.
 */
export function freshGuest(fill: (bytes: Uint8Array) => void = (b) => void crypto.getRandomValues(b)): GuestState {
  const bytes = new Uint8Array(16);
  fill(bytes);
  const id = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return { v: 1, guestId: id, name: `Settler-${id.slice(0, 4).toUpperCase()}` };
}

export const settingsSchema = z.object({
  v: z.literal(1),
  /** Colorblind-assist tile palette (plan §17.7 alternates land H2). */
  colorblindTiles: z.boolean(),
  /** Honor prefers-reduced-motion even when the OS setting is off. */
  reducedMotion: z.boolean(),
  /** Master audio mute (H12). */
  muted: z.boolean(),
  /** D212: first-party anonymous usage data. Default on (legitimate interest); DNT wins. */
  shareUsage: z.boolean(),
  /** The first-run board-gesture hint card was dismissed (H13 — shown once, ever). */
  gestureHintSeen: z.boolean(),
  /** D215: browser notifications (your turn / affordable offers) while the tab is hidden.
   *  Default ON — the browser permission is the real gate and starts ungranted. */
  notifications: z.boolean(),
});
export type SettingsState = z.infer<typeof settingsSchema>;

/**
 * Debug render knobs (D186) — deliberately a SEPARATE blob from `settings`.
 *
 * These are development controls, not player preferences, and `settings` carries choices a
 * player actually made (colourblind tiles, reduced motion, mute). Folding dev knobs into it
 * would mean every future debug flag needs another additive migration on the schema that
 * guards those choices — and the H12/H13 migrations exist precisely because getting that
 * wrong resets a real preference as a side effect. Its own key cannot do that: a corrupt or
 * unknown debug blob resets to defaults and costs nobody anything.
 */
export const debugSchema = z.object({
  v: z.literal(2),
  /** Graphics tier (render/detail.ts) — geometry detail, and whether the sun casts. */
  detail: z.enum(['common', 'fine', 'utmost']),
  /** Small frame-rate counter on the board (D190) — the one setting that applies live. */
  fps: z.boolean(),
  /** D249: the board reacts to the pointer (flock, wheat, trees). Also live. */
  liveBoard: z.boolean(),
  /** H22: the map view (D335). Live too, and never switched on by anything but the player. */
  mapView: z.boolean(),
  /** Draw the scatter props at all — a developer A/B. */
  props: z.boolean(),
  /** Live draw-call / triangle / frame-time readout over the board. */
  stats: z.boolean(),
});
export type DebugState = z.infer<typeof debugSchema>;

/**
 * Normalise a stored blob to the CURRENT tier names (D187 retiered, D188 dropped `passable`).
 *
 * The version stamp tracks the blob's SHAPE, which has not changed since v2 — four fields,
 * same types. What keeps changing is the tier VOCABULARY, and a version bump per rename would
 * be three schema versions for a setting that has never reached a player. So the migration
 * owns the vocabulary instead: every name this store has ever written maps to a tier that
 * exists today, and anything unrecognised lands on the default.
 *
 * Mapping is by PROP RATIO, always to the nearest tier that is NOT MORE EXPENSIVE than what
 * the player had. `passable` (0.3) sits between today's common (0.2) and fine (0.5) and maps
 * DOWN: a tier that disappears must never silently make somebody's game cost more.
 * v1's `shadowAutoUpdate` is dropped outright — shadows are a property of the tier now, and a
 * second, contradictory control for them would be exactly the dead knob this work was about.
 */
/**
 * `fine` MEANS DIFFERENT THINGS IN DIFFERENT VERSIONS, which is why this is keyed by version
 * and not by name alone: v1's `fine` was prop ratio 0.25, today's is 0.5. Mapping it by name
 * would have doubled the geometry of anyone who had chosen it — the exact thing the
 * never-more-expensive rule exists to prevent, and a bug a name-only table hides perfectly.
 */
const TIER_HISTORY: Readonly<Record<1 | 2, Readonly<Record<string, DebugState['detail']>>>> = Object.freeze({
  // v1 (D186), named for how much they CUT: minimal .02 / lean .04 / balanced .08 / fine .25 / full 1
  1: Object.freeze({ minimal: 'common', lean: 'common', balanced: 'common', fine: 'common', full: 'utmost' }),
  // v2 (D187), named for how they LOOK: common .15 / passable .3 / fine .6 / utmost 1
  2: Object.freeze({ common: 'common', passable: 'common', fine: 'fine', utmost: 'utmost' }),
});

export function migrateDebug(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const o = raw as Record<string, unknown>;
  const v = o['v'];
  if (v !== 1 && v !== 2) return o; // an unknown FUTURE version is left for the schema to reject
  const table = TIER_HISTORY[v];
  // `fps` arrived at D190; a blob written before it is stamped with the default rather than
  // failing the schema and resetting the player's tier as a side effect (the H12/H13 move).
  // D249 repeats the `fps` move for `liveBoard`: an older blob is STAMPED with the default
  // rather than failing the schema, which would reset the player's tier as a side effect.
  return {
    v: 2,
    detail: table[String(o['detail'])] ?? 'common',
    fps: o['fps'] ?? false,
    liveBoard: o['liveBoard'] ?? true,
    // H22 repeats it for `mapView`, OFF unless the player chose it
    mapView: o['mapView'] ?? false,
    props: o['props'] ?? true,
    stats: o['stats'] ?? false,
  };
}

export const sessionSchema = z.object({
  v: z.literal(1),
  authToken: z.string().optional(),
  resumeRoom: z.object({ code: z.string(), gameSessionToken: z.string() }).optional(),
});
export type SessionState = z.infer<typeof sessionSchema>;

/**
 * The guest's rank (H14/D201) — its OWN key, not a field on `guestSchema`.
 *
 * It is server-issued and server-signed: the client is a courier, never an author. Folding
 * it into the guest profile would put a tamper-evident blob in the same store as the freely
 * editable display name, and a schema rejection on the name would then throw the rank away
 * too. `everHeld` is the one part the client does own — it is a display memory (which
 * shields have stopped being blurred), not a claim about the ladder.
 */
export const rankSchema = z.object({
  v: z.literal(1),
  record: z
    .object({
      guestId: z.string(),
      season: z.string(),
      rating: z.number(),
      rd: z.number(),
      ratedGames: z.number().int().nonnegative(),
      issuedAt: z.number().int().nonnegative(),
      sig: z.string(),
    })
    .nullable(),
  /** Rank ids ever attained, so a shield already seen is never re-blurred. */
  everHeld: z.array(z.string()),
  /** The rank last shown to the player, so a promotion fires exactly once. */
  lastShown: z.string().nullable(),
});
export type RankState = z.infer<typeof rankSchema>;

/**
 * D216: what the Play panel remembers between visits — the tab you left it on and the
 * picks you made in it, so the front door re-offers your last game instead of asking the
 * same three questions again (the chess.com/Fortnite "memory replaces menus" rule).
 *
 * Its OWN key, for the reason `debugSchema` gives below: this blob changes on nearly every
 * visit, and a corrupt one must never reset the colourblind and reduced-motion choices a
 * player actually made. Losing it costs a re-pick and nothing else.
 *
 * `tab` starts at 'bots' because that is the only lane that can keep the ten-second promise
 * with nobody else online — it is the honest default at this population, not a guess.
 */
export const playSchema = z.object({
  /** D288: v2 — one "Find a game" card replaced the Ranked/Casual/Bots lanes. */
  v: z.literal(2),
  opponents: z.enum(['humans', 'bots']),
  /** A `SpeedId`, stored loosely: an unknown rung resolves to Standard rather than resetting. */
  speed: z.string(),
  size: z.enum(['2', '3-4', '5+']),
  /**
   * H20-S7: the board the last solo game was dealt on ('classic' or a scenario's menu id),
   * stored loosely like the speed — an unknown id plays classic rather than resetting the blob.
   */
  soloBoard: z.string().optional(),
  /**
   * D221: has this browser ever actually started a game from the menu? Set when a game is
   * STARTED, not when a pick is made — the one-tap "Play again" has to tell a returning player
   * from someone who merely had the defaults written on first paint.
   */
  played: z.boolean(),
});
export type PlayState = z.infer<typeof playSchema>;

export const STORAGE_KEYS = { guest: 'kolony.guest', settings: 'kolony.settings', session: 'kolony.session', debug: 'kolony.debug', rank: 'kolony.rank', play: 'kolony.play' } as const;

export const defaultSettings = (): SettingsState => ({ v: 1, colorblindTiles: false, reducedMotion: false, muted: false, shareUsage: true, gestureHintSeen: false, notifications: true });
export const defaultSession = (): SessionState => ({ v: 1 });
export const defaultRank = (): RankState => ({ v: 1, record: null, everHeld: [], lastShown: null });
/** D288: first-ever visit — humans (bots fill in), Standard, a 3–4 table. */
export const defaultPlay = (): PlayState => ({ v: 2, opponents: 'humans', speed: DEFAULT_TEMPO_ID, size: '3-4', played: false });

/**
 * The play blob's history, read forward so a returning player keeps their picks.
 * D221: a pre-landing-page blob has no `played` flag — stamp it. D288: a v1 blob's lane
 * becomes the opponents axis (Bots ⇒ bots only; Ranked and Casual ⇒ humans), its speed is
 * the speed of THAT lane, and its table size is read onto the new vocabulary.
 */
export function migratePlay(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const o = raw as Record<string, unknown>;
  const stamped = 'played' in o ? o : { ...o, played: false };
  if (stamped['v'] !== 1) return stamped;
  const bots = stamped['tab'] === 'bots';
  const legacySize = stamped['casualSize'];
  const out: Record<string, unknown> = {
    v: 2,
    opponents: bots ? 'bots' : 'humans',
    speed: String((bots ? stamped['soloSpeed'] : stamped['casualSpeed']) ?? DEFAULT_TEMPO_ID),
    // a solo table was always four seats; a casual size is mapped like the route maps it
    size: bots ? '3-4' : legacySize === '5-6' ? '5+' : '3-4',
    played: stamped['played'] === true,
  };
  if (typeof stamped['soloBoard'] === 'string') out['soloBoard'] = stamped['soloBoard'];
  return out;
}
/** D187: matches `defaultRenderSettings()` — the shipped tier, props on, no overlay. */
export const defaultDebug = (): DebugState => ({ v: 2, detail: 'common', fps: false, liveBoard: true, mapView: false, props: true, stats: false });

/**
 * Migrate a stored guest profile forward to the current schema. v0 (a pre-versioning blob with
 * no `v`, or an explicit `v:0`) is stamped `v:1` with its known fields carried over — data
 * preserved; unknown football-era fields (kit, per-event stats) are dropped. A blob already at
 * v1 passes through; an unknown FUTURE version is returned unchanged so the schema rejects it
 * → reset with `.bak`.
 */
/**
 * Carry a pre-H12 settings blob forward: it is `v:1` with no `muted`, which the schema would
 * reject — resetting the player's colourblind and reduced-motion choices as a side effect of
 * shipping audio. Stamping the default keeps their data. Same shape as `migrateGuest`.
 * H13 repeats the move for `gestureHintSeen` — a pre-H13 blob must not re-show choices
 * the player already made, nor be reset for lacking a flag that did not exist yet.
 */
export function migrateSettings(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const o = raw as Record<string, unknown>;
  const up = 'muted' in o ? o : { ...o, muted: false };
  const up2 = 'gestureHintSeen' in up ? up : { ...up, gestureHintSeen: false };
  // D212 repeats the move: a pre-telemetry blob must not reset the player's other choices
  // for lacking a flag that did not exist yet
  const up3 = 'shareUsage' in up2 ? up2 : { ...up2, shareUsage: true };
  // D215 repeats it again for the notifications toggle
  return 'notifications' in up3 ? up3 : { ...up3, notifications: true };
}

export function migrateGuest(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const o = raw as Record<string, unknown>;
  if (o['v'] === undefined || o['v'] === 0) {
    const up: Record<string, unknown> = { v: 1, guestId: o['guestId'] ?? '', name: o['name'] ?? '' };
    if (o['totals'] !== undefined) up['totals'] = o['totals'];
    return up;
  }
  return o;
}

export function makeStores(area: StorageArea) {
  return {
    settings: new VersionedStore(STORAGE_KEYS.settings, settingsSchema, defaultSettings, area, migrateSettings),
    session: new VersionedStore(STORAGE_KEYS.session, sessionSchema, defaultSession, area),
    debug: new VersionedStore(STORAGE_KEYS.debug, debugSchema, defaultDebug, area, migrateDebug),
    guest: (fallback: () => GuestState) => new VersionedStore(STORAGE_KEYS.guest, guestSchema, fallback, area, migrateGuest),
    rank: new VersionedStore(STORAGE_KEYS.rank, rankSchema, defaultRank, area),
    play: new VersionedStore(STORAGE_KEYS.play, playSchema, defaultPlay, area, migratePlay),
  };
}
